import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, Trash2, Tag, AlertTriangle, ChevronDown, ChevronUp, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClientRates() {
  const { token, user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [rateTables, setRateTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingTable, setDeletingTable] = useState(null);
  const [expandedTable, setExpandedTable] = useState(null);
  const [filterCustomerId, setFilterCustomerId] = useState('all');
  const fileInputRef = useRef(null);

  const [uploadForm, setUploadForm] = useState({
    customerId: '',
    label: '',
    isOutlier: false,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const isOfficeOrOwner = user?.role === 'OFFICE' || user?.role === 'OWNER';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [custRes, ratesRes] = await Promise.all([
        axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/client-rates`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setCustomers(custRes.data);
      setRateTables(ratesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getCustomerName = (customerId) => {
    const c = customers.find(c => c.id === customerId);
    return c ? c.name : 'Unknown';
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || !uploadForm.customerId || !uploadForm.label) {
      toast.error('Please fill all required fields and select a file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('customerId', uploadForm.customerId);
      formData.append('label', uploadForm.label);
      formData.append('isOutlier', uploadForm.isOutlier.toString());

      const res = await axios.post(`${API}/client-rates/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const { importedCount, errors } = res.data;
      toast.success(`Imported ${importedCount} rate entries`);
      if (errors && errors.length > 0) {
        toast.warning(`${errors.length} rows had errors`);
      }
      setUploadOpen(false);
      setSelectedFile(null);
      setUploadForm({ customerId: '', label: '', isOutlier: false });
      fetchData();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingTable) return;
    try {
      await axios.delete(`${API}/client-rates/${deletingTable.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Rate table deleted');
      setDeleteOpen(false);
      setDeletingTable(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete rate table');
    }
  };

  const filteredTables = filterCustomerId === 'all'
    ? rateTables
    : rateTables.filter(t => t.customerId === filterCustomerId);

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="text-center text-slate-600">Loading client rates...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8" data-testid="client-rates-page">
      <div className="flex flex-col gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">Client Rate Tables</h1>
          <p className="mt-1 md:mt-2 text-base md:text-lg text-slate-700">
            Manage customer-specific billing rates imported from Excel
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Select value={filterCustomerId} onValueChange={setFilterCustomerId}>
            <SelectTrigger className="w-full sm:w-[250px] rounded-sm border-2" data-testid="filter-customer-select">
              <SelectValue placeholder="Filter by customer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers</SelectItem>
              {customers.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isOfficeOrOwner && (
            <Button
              onClick={() => setUploadOpen(true)}
              data-testid="upload-rates-button"
              className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm shadow-sm transition-all active:scale-95 py-4 md:py-2 text-base w-full sm:w-auto"
            >
              <Upload className="w-5 h-5 mr-2" />
              Import Rate Table
            </Button>
          )}
        </div>
      </div>

      {filteredTables.length === 0 ? (
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardContent className="p-12 text-center">
            <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-600 mb-4">No client rate tables yet</p>
            {isOfficeOrOwner && (
              <Button
                onClick={() => setUploadOpen(true)}
                variant="outline"
                className="border-2 border-[#1A3668] text-[#1A3668] hover:bg-slate-50 rounded-sm"
              >
                Import your first rate table
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredTables.map((table) => (
            <Card
              key={table.id}
              data-testid={`rate-table-${table.id}`}
              className="border-slate-200 shadow-sm rounded-sm"
            >
              <CardContent className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-bold text-slate-900">{table.label}</h3>
                      {table.isOutlier && (
                        <Badge
                          variant="outline"
                          className="bg-amber-50 border-amber-300 text-amber-800 rounded-sm text-xs"
                          data-testid={`outlier-badge-${table.id}`}
                        >
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Outlier
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      <Tag className="w-3 h-3 inline mr-1" />
                      {getCustomerName(table.customerId)}
                      <span className="mx-2 text-slate-300">|</span>
                      {table.rates?.length || 0} rate entries
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setExpandedTable(expandedTable === table.id ? null : table.id)}
                      data-testid={`expand-table-${table.id}`}
                      className="border-2 border-slate-200 hover:border-[#1A3668] rounded-sm"
                    >
                      {expandedTable === table.id ? (
                        <ChevronUp className="w-4 h-4 mr-1" />
                      ) : (
                        <ChevronDown className="w-4 h-4 mr-1" />
                      )}
                      {expandedTable === table.id ? 'Collapse' : 'View Rates'}
                    </Button>
                    {isOfficeOrOwner && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setDeletingTable(table); setDeleteOpen(true); }}
                        data-testid={`delete-table-${table.id}`}
                        className="border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {expandedTable === table.id && table.rates && (
                  <div className="mt-4 border-t border-slate-200 pt-4 overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className="bg-slate-100 border-b-2 border-slate-200">
                          <th className="text-left p-3 font-bold uppercase tracking-wider text-slate-700 text-xs">Craft</th>
                          <th className="text-left p-3 font-bold uppercase tracking-wider text-slate-700 text-xs">Classification</th>
                          <th className="text-right p-3 font-bold uppercase tracking-wider text-slate-700 text-xs">ST Rate</th>
                          <th className="text-right p-3 font-bold uppercase tracking-wider text-slate-700 text-xs">OT Rate</th>
                          <th className="text-right p-3 font-bold uppercase tracking-wider text-slate-700 text-xs">DT Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {table.rates.map((rate, idx) => (
                          <tr key={`${rate.craftName}-${rate.classificationName}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="p-3 text-slate-700 font-medium">{rate.craftName}</td>
                            <td className="p-3 text-slate-900 font-bold">{rate.classificationName}</td>
                            <td className="p-3 text-right font-mono text-slate-700">${rate.stRate?.toFixed(2)}</td>
                            <td className="p-3 text-right font-mono text-slate-700">${rate.otRate?.toFixed(2)}</td>
                            <td className="p-3 text-right font-mono text-slate-700">${rate.dtRate?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="sm:max-w-[550px] rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <FileSpreadsheet className="w-6 h-6 text-[#1A3668]" />
              Import Rate Table
            </DialogTitle>
            <DialogDescription>
              Upload an Excel file with columns: Craft, Classification, ST Rate, OT Rate, DT Rate
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpload}>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Customer *</Label>
                <Select
                  value={uploadForm.customerId}
                  onValueChange={(v) => setUploadForm({ ...uploadForm, customerId: v })}
                >
                  <SelectTrigger className="mt-2 rounded-sm border-2" data-testid="upload-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Label *</Label>
                <Input
                  data-testid="upload-label-input"
                  value={uploadForm.label}
                  onChange={(e) => setUploadForm({ ...uploadForm, label: e.target.value })}
                  placeholder="e.g., Cardinal Mechanical 2026 Rates"
                  required
                  className="mt-2 rounded-sm border-2"
                />
                <p className="text-xs text-slate-500 mt-1">A descriptive name to identify this rate table</p>
              </div>
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-sm border border-amber-200">
                <div>
                  <p className="text-sm font-semibold text-amber-800">Mark as Outlier</p>
                  <p className="text-xs text-amber-600">Flag these rates as non-standard for office visibility</p>
                </div>
                <Switch
                  data-testid="upload-outlier-switch"
                  checked={uploadForm.isOutlier}
                  onCheckedChange={(v) => setUploadForm({ ...uploadForm, isOutlier: v })}
                />
              </div>
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Excel File *</Label>
                <div
                  className="mt-2 border-2 border-dashed border-slate-300 rounded-sm p-6 text-center cursor-pointer hover:border-[#1A3668] transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-file-dropzone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    data-testid="upload-file-input"
                  />
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileSpreadsheet className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium text-slate-800">{selectedFile.name}</span>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-600">Click to select an Excel file</p>
                      <p className="text-xs text-slate-400 mt-1">.xlsx files only</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-slate-50 p-3 rounded-sm border border-slate-200">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Expected Format</p>
                <div className="overflow-x-auto">
                  <table className="text-xs w-full border-collapse">
                    <thead>
                      <tr className="bg-slate-200">
                        <th className="p-1.5 text-left">Craft</th>
                        <th className="p-1.5 text-left">Classification</th>
                        <th className="p-1.5 text-right">ST Rate</th>
                        <th className="p-1.5 text-right">OT Rate</th>
                        <th className="p-1.5 text-right">DT Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white">
                        <td className="p-1.5 font-mono">Pipefitter</td>
                        <td className="p-1.5 font-mono">Journeyman</td>
                        <td className="p-1.5 text-right font-mono">65.00</td>
                        <td className="p-1.5 text-right font-mono">97.50</td>
                        <td className="p-1.5 text-right font-mono">130.00</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setUploadOpen(false); setSelectedFile(null); }}
                className="border-2 border-slate-200 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="submit-upload-button"
                disabled={uploading}
                className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm"
              >
                {uploading ? 'Importing...' : 'Import Rates'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Rate Table</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deletingTable?.label}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="confirm-delete-rate-table"
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
